import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  enabledMaterialOperations,
  materialCategories,
  materialCategoryLabel,
  materialDieCutOptions,
  materialDimensionUnits,
  materialDurationOptions,
  materialFileExtensions,
  materialMockupOptions,
  materialOperations,
  materialRegionalHubs,
  materialTextCharacterOptions,
  materialTimeUnits,
  type MaterialDimensionUnit,
  type MaterialDto,
  type MaterialFileExtension,
  type MaterialHub,
  type MaterialOperation,
  type MaterialSubmission,
} from '../materials'
import { MaterialApiError } from '../materialsClient'
import type { MaterialCategory } from '../types'

interface Props {
  initialOperation: MaterialOperation | ''
  initialHub: MaterialHub | ''
  onClose: () => void
  onCreate: (submission: MaterialSubmission) => Promise<MaterialDto>
  onCreated: (material: MaterialDto) => void
}

function nullableNumber(value: FormDataEntryValue | null) {
  const text = String(value ?? '').trim()
  return text ? Number(text) : null
}

function focusableElements(container: HTMLElement) {
  return [...container.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
}

export function MaterialRegistrationModal({ initialOperation, initialHub, onClose, onCreate, onCreated }: Props) {
  const dialog = useRef<HTMLDivElement>(null)
  const closeButton = useRef<HTMLButtonElement>(null)
  const [operation, setOperation] = useState<MaterialOperation | ''>(initialOperation)
  const [category, setCategory] = useState<MaterialCategory | ''>('')
  const [textCharacterCount, setTextCharacterCount] = useState('')
  const [estimatedTimeUnit, setEstimatedTimeUnit] = useState<(typeof materialTimeUnits)[number]>('horas')
  const [dimensionUnit, setDimensionUnit] = useState<MaterialDimensionUnit | ''>('')
  const [durationSeconds, setDurationSeconds] = useState('')
  const [fileExtension, setFileExtension] = useState<MaterialFileExtension | ''>('')
  const [hubType, setHubType] = useState<'Matriz' | 'Regional' | ''>(initialHub === 'Matriz' ? 'Matriz' : initialHub ? 'Regional' : '')
  const [regionalHubs, setRegionalHubs] = useState<MaterialHub[]>(initialHub && initialHub !== 'Matriz' ? [initialHub] : [])
  const [status, setStatus] = useState<'idle' | 'sending' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [cooldownSeconds, setCooldownSeconds] = useState(0)

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    const frame = window.requestAnimationFrame(() => closeButton.current?.focus())
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialog.current) return
      const focusable = focusableElements(dialog.current)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown, true)
      previouslyFocused?.focus()
    }
  }, [onClose])

  useEffect(() => {
    if (cooldownSeconds <= 0) return
    const timer = window.setTimeout(() => setCooldownSeconds(value => Math.max(0, value - 1)), 1000)
    return () => window.clearTimeout(timer)
  }, [cooldownSeconds])

  function toggleRegionalHub(hub: MaterialHub) {
    setRegionalHubs(current => current.includes(hub) ? current.filter(item => item !== hub) : [...current, hub])
    setStatus('idle')
    setMessage('')
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    if (cooldownSeconds > 0) {
      setStatus('error')
      setMessage(`Aguarde ${cooldownSeconds} segundos antes de cadastrar outro material.`)
      return
    }
    if (!operation) {
      setStatus('error')
      setMessage('Selecione a operação.')
      const field = form.elements.namedItem('operation')
      if (field instanceof HTMLElement) field.focus()
      return
    }
    if (!category) {
      setStatus('error')
      setMessage('Selecione a categoria do material.')
      const field = form.elements.namedItem('materialCategory')
      if (field instanceof HTMLElement) field.focus()
      return
    }
    if (category !== 'Textos' && !hubType) {
      setStatus('error'); setMessage('Selecione a HUB responsável pelo material.'); return
    }
    if (category !== 'Textos' && hubType === 'Regional' && regionalHubs.length === 0) {
      setStatus('error'); setMessage('Selecione ao menos uma HUB Regional responsável pelo material.'); return
    }
    const values = new FormData(form)
    const width = nullableNumber(values.get('width'))
    const height = nullableNumber(values.get('height'))
    if (category !== 'Textos' && (width !== null || height !== null) && !dimensionUnit) {
      setStatus('error'); setMessage('Selecione a unidade das dimensões informadas.'); return
    }
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    const isText = category === 'Textos'
    const estimatedTimeValue = nullableNumber(values.get('estimatedTimeValue'))
    const payload: MaterialSubmission = {
      operation,
      materialCategory: category,
      textCharacterCount: isText ? nullableNumber(textCharacterCount) : null,
      materialName: String(values.get('materialName') ?? '').trim(),
      estimatedTimeValue,
      estimatedTimeUnit: estimatedTimeValue === null ? null : estimatedTimeUnit,
      dimensionUnit: isText ? null : dimensionUnit || null,
      width: isText ? null : width,
      height: isText ? null : height,
      durationSeconds: nullableNumber(durationSeconds),
      fileExtension: isText ? null : fileExtension || null,
      fileSize: isText ? null : String(values.get('fileSize') ?? '').trim() || null,
      dieCut: isText ? null : (String(values.get('dieCut') ?? '') as MaterialSubmission['dieCut']) || null,
      mockup: isText ? null : (String(values.get('mockup') ?? '') as MaterialSubmission['mockup']) || null,
      hubs: isText ? null : hubType === 'Matriz' ? ['Matriz'] : regionalHubs,
    }

    setStatus('sending')
    setMessage('')
    try {
      const material = await onCreate(payload)
      onCreated(material)
    } catch (error) {
      setStatus('error')
      if (error instanceof MaterialApiError) {
        if (error.status === 429) setCooldownSeconds(Math.max(1, Number(error.retryAfter) || 25))
        setMessage(error.message)
      } else {
        setMessage('Não foi possível conectar à base de dados. Tente novamente.')
      }
    }
  }

  const isText = category === 'Textos'
  return <div className="material-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
    <div ref={dialog} className="material-modal" role="dialog" aria-modal="true" aria-labelledby="material-modal-title">
      <header className="material-modal-head">
        <div><span>Novo cadastro</span><h2 id="material-modal-title">Cadastrar material</h2><p>Inclua as especificações na base compartilhada do StarTracker.</p></div>
        <button ref={closeButton} type="button" onClick={onClose} aria-label="Fechar cadastro de material" title="Fechar">×</button>
      </header>
      <form className="material-registration-form" onSubmit={submit}>
        <label className="material-registration-field material-registration-full"><span>Operação <b>*</b></span><select name="operation" value={operation} onChange={event => { setOperation(event.target.value as MaterialOperation); setStatus('idle'); setMessage('') }}><option value="">Selecione uma operação</option>{materialOperations.map(item => <option key={item} value={item} disabled={!(enabledMaterialOperations as readonly string[]).includes(item)}>{item}{!(enabledMaterialOperations as readonly string[]).includes(item) ? ' (em breve)' : ''}</option>)}</select><small>Gitanes e Menil serão liberadas quando houver suporte no banco.</small></label>
        <label className="material-registration-field material-registration-full"><span>Categoria do material <b>*</b></span><select name="materialCategory" value={category} onChange={event => { setCategory(event.target.value as MaterialCategory | ''); setTextCharacterCount(''); setStatus('idle'); setMessage('') }} disabled={!operation}><option value="">Selecione uma categoria</option>{materialCategories.map(item => <option key={item} value={item}>{materialCategoryLabel(item)}</option>)}</select></label>

        {category && <>
          {isText && <label className="material-registration-field material-registration-full"><span>Caracteres <small>(opcional)</small></span><select value={textCharacterCount} onChange={event => setTextCharacterCount(event.target.value)}><option value="">Selecione</option>{materialTextCharacterOptions.map(option => <option key={option}>{option}</option>)}</select></label>}
          <label className="material-registration-field material-registration-full"><span>Nome do material <b>*</b></span><input name="materialName" required minLength={2} maxLength={160} placeholder="Ex.: Banner campanha de verão"/></label>
          <div className="material-registration-field material-registration-full"><span>Tempo estimado para confecção <small>(opcional)</small></span><div className="material-registration-split"><input name="estimatedTimeValue" type="number" min="0.1" max="9999" step="0.1" inputMode="decimal" placeholder="Ex.: 4" aria-label="Tempo estimado"/><select value={estimatedTimeUnit} onChange={event => setEstimatedTimeUnit(event.target.value as typeof estimatedTimeUnit)} aria-label="Unidade de tempo">{materialTimeUnits.map(unit => <option key={unit}>{unit}</option>)}</select></div></div>
          {!isText && <fieldset className="material-registration-field material-registration-full"><legend>Dimensões do material <small>(opcional)</small></legend><div className="material-registration-segments three">{materialDimensionUnits.map(unit => <label key={unit}><input type="radio" name="dimensionUnit" value={unit} checked={dimensionUnit === unit} onChange={() => setDimensionUnit(unit)}/><span>{unit === 'px' ? 'Pixel' : unit.toUpperCase()}</span></label>)}</div><div className="material-registration-dimensions"><label><span>Largura</span><input name="width" type="number" min={dimensionUnit === 'px' ? 1 : 0.01} max="999999" step={dimensionUnit === 'px' ? 1 : 0.01} inputMode="decimal" placeholder="0"/></label><i>×</i><label><span>Altura</span><input name="height" type="number" min={dimensionUnit === 'px' ? 1 : 0.01} max="999999" step={dimensionUnit === 'px' ? 1 : 0.01} inputMode="decimal" placeholder="0"/></label></div></fieldset>}
          <label className="material-registration-field material-registration-full"><span>Minutagem <small>(opcional)</small></span><select value={durationSeconds} onChange={event => setDurationSeconds(event.target.value)}><option value="">Selecione</option>{materialDurationOptions.map(option => <option key={option} value={option}>{option}&quot;</option>)}</select></label>
          {!isText && <>
            <label className="material-registration-field"><span>Extensão do arquivo <small>(opcional)</small></span><select value={fileExtension} onChange={event => setFileExtension(event.target.value as MaterialFileExtension | '')}><option value="">Selecione</option>{materialFileExtensions.map(option => <option key={option} value={option}>.{option}</option>)}</select></label>
            <label className="material-registration-field"><span>Tamanho <small>(opcional)</small></span><input name="fileSize" maxLength={30} placeholder="Ex.: 10 MB"/></label>
            <fieldset className="material-registration-field"><legend>Faca <small>(opcional)</small></legend><div className="material-registration-segments">{materialDieCutOptions.map(option => <label key={option}><input type="radio" name="dieCut" value={option}/><span>{option}</span></label>)}</div></fieldset>
            <fieldset className="material-registration-field"><legend>Mockup? <small>(opcional)</small></legend><div className="material-registration-segments">{materialMockupOptions.map(option => <label key={option}><input type="radio" name="mockup" value={option}/><span>{option}</span></label>)}</div></fieldset>
            <fieldset className="material-registration-field material-registration-full material-registration-hubs"><legend>HUB <b>*</b></legend><div className="material-registration-segments"><label><input type="radio" name="hubType" checked={hubType === 'Matriz'} onChange={() => { setHubType('Matriz'); setRegionalHubs([]) }}/><span>Matriz</span></label><label><input type="radio" name="hubType" checked={hubType === 'Regional'} onChange={() => setHubType('Regional')}/><span>Regional</span></label></div>{hubType === 'Regional' && <div className="material-hub-options" aria-label="HUBs Regionais">{materialRegionalHubs.map(hub => <label key={hub}><input type="checkbox" checked={regionalHubs.includes(hub)} onChange={() => toggleRegionalHub(hub)}/><span>{hub}</span></label>)}</div>}</fieldset>
          </>}
        </>}

        {message && <div className="material-registration-status material-registration-full" role="alert">{message}</div>}
        <footer className="material-registration-actions material-registration-full"><small>* Campos obrigatórios</small><div><button type="button" className="secondary" onClick={onClose}>Fechar</button><button type="submit" className="primary" disabled={!category || status === 'sending' || cooldownSeconds > 0}>{status === 'sending' ? 'Salvando…' : cooldownSeconds > 0 ? `Aguarde ${cooldownSeconds}s` : 'Cadastrar material'}</button></div></footer>
      </form>
    </div>
  </div>
}
